/**
 * UnlockIntegrationPanel
 *
 * Shown when a customer clicks a locked (subscription-gated) integration card.
 * Presents TWO clear paths so there is zero confusion about how to get the integration:
 *
 *   PATH A — Through a marketplace (Shopify App Store, BigCommerce, WooCommerce, etc.)
 *             Shown only when the integration has marketplace listings.
 *             Links directly to the store listing and explains that billing lives there.
 *
 *   PATH B — Direct from Velaro
 *             Always shown. Pre-fills the UpgradeRequestPanel with the integration name.
 *             For customers who signed up directly with Velaro (not through a marketplace).
 *
 * HOW TO ADD A NEW INTEGRATION:
 *   1. Add the integration to Applications.ts (id + metadata).
 *   2. Add one entry to integrationGateMap in IntegrationsLanding.tsx.
 *   3. Optionally add marketplace links to integrationMarketplaceLinks in Applications.ts.
 *   4. This panel picks it all up automatically — no changes needed here.
 */
import React, { useState } from "react";
import { integrationMarketplaceLinks, AppId } from "../Applications";
import UpgradeRequestPanel from "../../Billing/UpgradeRequestPanel";

interface Props {
  /** App ID from Applications.ts (used to look up marketplace links) */
  appId: AppId | string;
  /** Display name of the integration (pre-fills the upgrade form) */
  appName: string;
  /** Called when the user dismisses the panel */
  onClose: () => void;
}

export default function UnlockIntegrationPanel({ appId, appName, onClose }: Props) {
  const [activeSection, setActiveSection] = useState<"choose" | "direct">("choose");

  const marketplaceLinks = integrationMarketplaceLinks[appId as AppId] ?? [];
  const hasMarketplaceOptions = marketplaceLinks.length > 0;

  // ── Direct contact form ───────────────────────────────────────────────
  if (activeSection === "direct") {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-2 mb-5">
          <button
            onClick={() => setActiveSection("choose")}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Back"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <span className="text-sm font-medium text-slate-700">Contact Velaro directly</span>
        </div>
        <UpgradeRequestPanel
          prefilledFeature={appName}
          onClose={onClose}
        />
      </div>
    );
  }

  // ── Choose path ───────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      {/* Integration description */}
      <div className="rounded-xl border border-amber-100 bg-amber-50 p-4 flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600" aria-hidden="true">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>
        <div>
          <p className="text-sm font-semibold text-amber-800">{appName} is not included in your current plan</p>
          <p className="text-xs text-amber-600 mt-1">
            Choose how you'd like to add it below. If you're unsure, select "Contact Velaro directly."
          </p>
        </div>
      </div>

      {/* ── Path A: Marketplace ─────────────────────────────────────────── */}
      {hasMarketplaceOptions && (
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            Did you sign up through a marketplace?
          </p>
          <div className="space-y-2">
            {marketplaceLinks.map((link) => (
              <a
                key={link.storeUrl}
                href={link.storeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-slate-200 bg-white hover:border-cornflower-blue-300 hover:shadow-sm transition-all group"
              >
                <div>
                  <span className="text-sm font-medium text-slate-800 group-hover:text-primary-active">
                    {link.storeLabel}
                  </span>
                  {link.note && (
                    <p className="text-xs text-slate-400 mt-0.5">{link.note}</p>
                  )}
                </div>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400 group-hover:text-primary flex-shrink-0" aria-hidden="true">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                  <polyline points="15 3 21 3 21 9"/>
                  <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Divider between paths */}
      {hasMarketplaceOptions && (
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-xs text-slate-400">or</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>
      )}

      {/* ── Path B: Direct from Velaro ──────────────────────────────────── */}
      <div>
        {hasMarketplaceOptions && (
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            Direct Velaro customer?
          </p>
        )}
        <button
          onClick={() => setActiveSection("direct")}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-slate-200 bg-white hover:border-cornflower-blue-300 hover:shadow-sm transition-all group text-left"
        >
          <div>
            <span className="text-sm font-medium text-slate-800 group-hover:text-primary-active">
              Contact Velaro to unlock {appName}
            </span>
            <p className="text-xs text-slate-400 mt-0.5">
              Your account manager will reach out within 1 business day.
            </p>
          </div>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400 group-hover:text-primary flex-shrink-0" aria-hidden="true">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
      </div>

      {/* Help text */}
      <p className="text-xs text-slate-400 text-center pt-1">
        Not sure which applies to you?{" "}
        <button
          onClick={() => setActiveSection("direct")}
          className="text-primary hover:underline"
        >
          Contact us
        </button>
        {" "}and we'll sort it out.
      </p>
    </div>
  );
}
